import React, {useEffect, useRef, useState} from 'react';
import {StoryblocksMediaItem} from '../../../data/models/Storyblocks/StoryblocksMediaItem';
import StoryblocksMediaCard from '../components/StoryblocksMediaCard';
import styles from './BrowseStoryblocksUI.module.css';
import LoadingSpinner from '../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {appText} from '../../../assets/text/appText';
import Button from '../../../uiToolkit/Buttons/Button';
import SearchInput2 from '../../../uiToolkit/Inputs/SearchInput2/SearchInput2';

interface Props {
  mediaItems: StoryblocksMediaItem[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onClose: () => void;
  search: string;
  onSearch: (search: string) => void;
  onSelect: (storyBlocksVideoId: string) => void;
}

const StoryblocksView: React.FC<Props> = ({
  mediaItems,
  isLoading,
  hasMore,
  onLoadMore,
  onClose,
  search,
  onSearch,
  onSelect,
}) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [tmpSearch, setTmpSearch] = useState<string>(search);
  const [selectedItem, setSelectedItem] = useState<
    StoryblocksMediaItem | undefined
  >(undefined);

  useEffect(() => {
    if (!hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      {threshold: 1.0},
    );

    const el = bottomRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [isLoading, hasMore, onLoadMore]);

  const onSelectClick = () => {
    if (selectedItem) {
      onSelect(selectedItem?.id.toString());
      onClose();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <SearchInput2
          value={tmpSearch}
          onSearch={onSearch}
          onTyping={setTmpSearch}
          disabled={isLoading}
        />
      </div>
      <div className={styles.content}>
        {mediaItems.map(item => (
          <StoryblocksMediaCard
            item={item}
            key={item.id}
            onClick={setSelectedItem}
            isSelected={item.id === selectedItem?.id}
          />
        ))}

        {mediaItems.length > 0 && <div ref={bottomRef} style={{height: 1}} />}
        {isLoading && (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        )}
        {!hasMore && mediaItems.length === 0 && !isLoading && (
          <p className={styles.emptyError}>
            {appText.noMoreItems}
            <span>{appText.noMoreItemsDescription}</span>
          </p>
        )}
      </div>
      <div className={styles.footer}>
        <Button title={appText.cancel} uiType="text" onClick={onClose} />
        <Button
          title={appText.select}
          disabled={!selectedItem || isLoading}
          onClick={onSelectClick}
        />
      </div>
    </div>
  );
};

export default StoryblocksView;
